import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from '@hooks/useToast';
import { Button } from '@components/ui/button';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@contexts/app';
import { useCreateWebPush } from '@services/webPushNotifications/create';
import { useRemoveWebPush } from '@services/webPushNotifications/remove';
import { useGetWebPush } from '@services/webPushNotifications/get';
import { WebpushResp } from '@services/webPushNotifications/types';

const DISMISS_KEY = 'pushBannerDismissed';

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

export const PushNotifications = () => {
  const { t } = useTranslation('app');
  const [dismissed, setDismissed] = useState(readDismissed);
  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {}
    setDismissed(true);
  };
  const { setData } = useAppContext();
  const onSettled = (resp: WebpushResp) => {
    if (resp.success && resp.pushEnabled) {
      toast({
        variant: 'default',
        title: t('navigation.push.confirmed'),
      });
      updatePushEnabledData(true);
    } else {
      updatePushEnabledData(false);
    }
  };
  const { mutate: create } = useCreateWebPush({ callback: onSettled });
  const { mutate: destroy } = useRemoveWebPush({ callback: onSettled });
  const { data } = useGetWebPush();

  const updatePushEnabledData = (pushEnabled: boolean) => {
    if (!setData) return;

    setData((ps) => ({
      ...ps,
      pushEnabled,
    }));
  };

  if (!window.location.protocol.includes('s')) return null;
  function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      // eslint-disable-next-line immutable/no-mutation
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  const enablePushNotifications = async () => {
    if (data?.pushEnabled) destroy();
    if ('Notification' in window) {
      await Notification.requestPermission().then((permission) => {
        // If the user accepts, let's create a notification
        if (permission === 'granted') {
          try {
            const options = {
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(import.meta.env.VITE_PUSH_KEY as string),
            };
            void navigator.serviceWorker.ready.then(async (serviceWorkerRegistration) => {
              await serviceWorkerRegistration.pushManager
                .subscribe(options)
                .then((pushSubscription) => {
                  const { endpoint, keys } = pushSubscription.toJSON();
                  if (typeof endpoint !== 'string' || !keys) return;
                  const { auth, p256dh } = keys;
                  create({
                    webpushNotifications: {
                      endpoint: endpoint,
                      auth: auth,
                      p256dh: p256dh,
                    },
                  });
                })
                .finally(() => {
                  window.location.reload();
                });
            });
          } catch (error) {
            console.error(error);
          }
        } else {
          destroy();
        }
      });
    }
  };

  if (
    !dismissed &&
    data &&
    'Notification' in window &&
    (Notification.permission !== 'granted' || !data.pushEnabled) &&
    import.meta.env.VITE_PUSH_KEY
  )
    return (
      <div className="bg-warning">
        <div className="container flex items-center gap-2 py-1 pl-4 pr-1 text-white">
          <span className="min-w-0 flex-1 font-inter text-sm font-medium leading-tight">
            {t('navigation.push.info')}
          </span>
          <Button
            variant="secondary"
            size="sm"
            className="shrink-0"
            onClick={() => void enablePushNotifications()}>
            {t('navigation.push.action')}
          </Button>
          <button
            type="button"
            aria-label={t('navigation.push.dismiss')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md hover:bg-white/20"
            onClick={dismiss}>
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    );

  return null;
};
